import React from 'react';
import { BaseSize, TextWeight, TextColor } from '@/shared/types/text';
import { cn } from '@/shared/utils/cn';
import { SYSTEM_COLORS } from './constants';

export interface CustomTextProps {
  /** Tamaño base del texto: 2xs, xs, sm, md, lg, xl, 2xl, 3xl, 4xl, 5xl */
  size: BaseSize;
  /** Peso de la fuente */
  weight?: TextWeight;
  /** Contenido del texto */
  children: React.ReactNode;
  /** Clase CSS adicional */
  className?: string;
  /** Elemento HTML a renderizar (por defecto: span) */
  as?: keyof JSX.IntrinsicElements;
  /** Color del texto: puede ser un color del sistema de diseño o un color CSS personalizado */
  color?: TextColor | (string & {});
  /** @deprecated Usa 'color' en su lugar */
  textColor?: TextColor;
}

export const CustomText = ({
  size,
  weight = 'regular',
  children,
  className,
  as = 'span',
  color,
  textColor,
  ...props
}: CustomTextProps) => {
  const Component = as;

  // Construir las clases para tamaños base
  const getTypographyClasses = (): string => {
    const classes: string[] = [];

    // Usar valores base (2xs, xs, sm, md, lg, xl, 2xl, etc.)
    classes.push(`ui-text-${size}`);

    // Aplicar weight base
    if (weight !== 'regular') {
      classes.push(`ui-font-${weight}`);
    }

    return classes.join(' ');
  };

  const typographyClasses = getTypographyClasses();

  // Verificar si el color es del sistema de diseño
  const isSystemColor = color && SYSTEM_COLORS.includes(color as TextColor);

  // Estilos inline solo para colores CSS personalizados
  const style = color && !isSystemColor ? { color } : undefined;

  // Color del sistema de diseño (sin color por defecto para CustomText)
  const getSystemColorClass = (): string => {
    if (isSystemColor) return `ui-text-${color}`;
    if (color) return '';
    if (textColor) return `ui-text-${textColor}`;
    return ''; // Sin color por defecto
  };

  const systemColorClass = getSystemColorClass();

  return (
    <Component
      className={cn('ui-imj', typographyClasses, systemColorClass, className)}
      style={style}
      {...props}
    >
      {children}
    </Component>
  );
};

CustomText.displayName = 'CustomText';
